import React from "react";
import {
  IconButton,
  TableCell,
  TableRow,
  useTheme,
  CircularProgress,
} from "@mui/material";
import Tooltip from "components/base/Tooltip";
import { RegionInterface } from "shared/utils";
import { TemplateColumnInterface, StatusInterface } from "./TableInterfaces";
import {
  StyledTableSortLabel,
  StatusContainer,
  Dot,
} from "components/TableStyles";
import {
  EditOutlined,
  ArchiveOutlined,
  FileCopyOutlined,
} from "@mui/icons-material";

export const templateColumn = (
  col: TemplateColumnInterface,
  key: string,
  orderBy: string,
  orderDirection: "asc" | "desc",
  onClickCallback: (attribute: string) => void,
) => (
  <TableCell
    key={key}
    sortDirection={orderBy === col?.attribute ? orderDirection : false}
  >
    <StyledTableSortLabel
      active={orderBy === col.attribute}
      direction={orderBy === col.attribute ? orderDirection : "asc"}
      onClick={() => onClickCallback(col.attribute)}
    >
      {col.label}
    </StyledTableSortLabel>
  </TableCell>
);

interface StatusProps {
  color: string;
  children?: React.ReactNode;
}

export const StatusTrafficLight = (props: StatusProps) => (
  <StatusContainer>
    <Dot
      style={{
        marginRight: "12px",
        backgroundColor: props.color,
      }}
    />
    {props.children}
  </StatusContainer>
);

interface IconButtonProps {
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  placement?:
    | "bottom-end"
    | "bottom-start"
    | "bottom"
    | "left-end"
    | "left-start"
    | "left"
    | "right-end"
    | "right-start"
    | "right"
    | "top-end"
    | "top-start"
    | "top";
  id?: string;
}

export const EditIcon = ({ onClick, placement, id }: IconButtonProps) => (
  <Tooltip title="Edit" placement={placement || "top"}>
    <span>
      <IconButton onClick={onClick} data-test-id={id} size="large">
        <EditOutlined />
      </IconButton>
    </span>
  </Tooltip>
);

export const ArchiveIcon = ({ onClick, placement, id }: IconButtonProps) => (
  <Tooltip title="Archive" placement={placement || "top"}>
    <span>
      <IconButton onClick={onClick} data-test-id={id} size="large">
        <ArchiveOutlined />
      </IconButton>
    </span>
  </Tooltip>
);

export const CloneIcon = ({ onClick, placement, id }: IconButtonProps) => (
  <Tooltip title="Clone" placement={placement || "top"}>
    <span>
      <IconButton onClick={onClick} data-test-id={id} size="large">
        <FileCopyOutlined />
      </IconButton>
    </span>
  </Tooltip>
);

export const getCountryList = (countries: RegionInterface[]) =>
  countries.length ? countries.map((item) => item.name).join(", ") : "";

export const ROWS_PER_PAGE = [100, 150, 200];

interface PlaceholderRowProps {
  loading: boolean;
  colSpan: number;
}

export const PlaceholderRow = ({ loading, colSpan }: PlaceholderRowProps) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center">
      {loading ? <CircularProgress color="primary" /> : "No results found!"}
    </TableCell>
  </TableRow>
);

const StatusLookup: StatusInterface = {
  approve: "Approved",
  test: "Test",
  draft: "Draft",
  requested: "Requested",
};

export const BeautifyStatus = (status: string): string =>
  StatusLookup[status] || status;

export const ColorFromStatus = (status: string, theme: any) => {
  if (status === "approve") return theme.palette.success.main;
  if (status === "test") return theme.palette.info.main;
  if (status === "draft") return theme.palette.grey[600];
  if (status === "requested") return theme.palette.warning.main;
  return "black";
};
